import { actionEmitter, EmitEvent } from "../config/ActionEmitter";
import { VersionStatus } from "../config/Enums";
import {
  IActionResult,
  IApiResponse,
  IComponent,
  IFooter,
  IHeader,
  IJSTreeNode,
  ILanguage,
  IPage,
  IPageHierarchy,
  IVersion,
  IVersionSection,
  ILayoutTemplate,
  ICloneNewPage
} from "../models/Interfaces";
import { AddUpdatePageCommand } from "../models/content/AddUpdatePageCommand";
import { AddVersionCommand } from "../models/content/AddVersionCommand";
import { BaseService } from "./BaseService";

export class ContentService extends BaseService {

  // PAGES
  public getPagesTreeData = async (): Promise<IJSTreeNode[]> => {
    let pages = [];
    if (this.siteKey) {
      const url = `/SiteAdmin/UrlHierarchy/${this.siteKey}`;
      const res = await this._ajaxService.Get<any>(url, true);

      if (res?.length > 0) {
        pages = res;

      }
    }
    return pages;
  }

  public getPageDetails = async (pageid: number): Promise<IPage> => {
    let page = null;

    if (this.siteKey) {
      const url = `/SiteAdmin/GetPage/${this.siteKey}/${pageid}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.Success && res?.ReturnData) {
        page = res.ReturnData;
      } else {
        this.EmitDataError(res, "Unable to retrieve page details");
      }
    }
    return page;
  }

  public getPagesForLanguage = async (lang: string): Promise<IPageHierarchy[]> => {
    let pages = [];

    if (this.siteKey) {
      const url = `/SiteAdmin/GetPageHierarchy/${this.siteKey}/${lang}/${true}`
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.ReturnData) {
        pages = res.ReturnData;
      }
    }
    return pages;
  }

  public getAllPages = async () : Promise<Array<IPage>> => {
    if (this.siteKey) {
      const url: string = `/SiteAdmin/GetPages/${this.siteKey}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.StatusCode === 200 && res.ReturnData) {
        return res.ReturnData;
      }
    }
    return [];
  }

  public addDefaultHomePage = async (lang: string) : Promise<boolean> => {
    const defaultpage = new AddUpdatePageCommand();
    defaultpage.Language = lang;
    defaultpage.Alias = "Homepage";
    defaultpage.Title = "Homepage";
    defaultpage.Url = "/";
    defaultpage.IsNewsPage = false;
    defaultpage.ExcludeSiteMap = false;
    defaultpage.Slug = "/";

    const res = await this.addUpdatePage(defaultpage);
    if (res.Success) {
      return true;
    }

    return false;
  }

  public addUpdatePage = async(command: AddUpdatePageCommand): Promise<IActionResult> => {
    const result: IActionResult = {
      Success: false,
      Message: "Failed to save page details"
    };

    if (this.siteKey && command) {
      const url: string = "/SiteAdmin/SavePage";
      command.SiteId = this.siteKey;
      const res = await this._ajaxService.Post<IApiResponse>(url, command, true);

      if (res?.Success) {
        result.Success = true;
        result.Message = this.getMessage(res, "Page details saved");
        result.Data = res?.ReturnData?.Id;
      } else {
        result.Message = this.getMessage(res, result.Message);
      }
    }
    return result;
  }

  public deletePage = async (pageid: number): Promise<IActionResult> => {
    const siteKey = this.siteKey;
    const result: IActionResult = {
      Success: false,
      Message: "Failed to remove the page from your site",
    };

    if (siteKey) {
      const url: string = `/SiteAdmin/DeletePage/${siteKey}/${pageid}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.Success) {
        result.Success = true;
        result.Message = this.getMessage(res, "Page removed from your site");
      } else {
        result.Message = this.getMessage(res, result.Message);
      }
    }
    return result;
  }

  // VERSIONS
  public getVersion = async (versionid: number): Promise<IVersion | null> => {
    let version: IVersion | null = null;
    if (this.siteKey) {
      const url: string = `/SiteAdmin/GetPageVersion/${this.siteKey}/${versionid}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.Success) {
        version = res.ReturnData;

      }
    }
    return version;
  }

  public getVersionSections = async (versionid: number): Promise<IVersionSection[]> => {
    let sections: IVersionSection[] = [];

    if (this.siteKey) {
      const url: string = `/SiteAdmin/GetSections/${this.siteKey}/${versionid}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.Success) {
        sections = res.ReturnData;
      }
    }

    return sections;
  }

  public addVersion = async (pageid: number): Promise<IVersion | null> => {
    if (this.siteKey) {
      const url: string = `/SiteAdmin/SavePageVersion`;
      const cmd = new AddVersionCommand();
      cmd.PageId = pageid;
      cmd.SiteId = this.siteKey;
      cmd.Status = VersionStatus.Draft;

      const res = await this._ajaxService.Post<IApiResponse>(url, cmd, true);

      if (res?.StatusCode === 200) {
        actionEmitter.emit(EmitEvent.Result, res.Success, res.Messages[0]);
        return res.ReturnData;
      } else {
        this.EmitDataError(res, "Failed to add a new version");
      }
    }
    return null;
  }

  public saveVersion = async (version: IVersion): Promise<IVersion | null> => {
    if (version) {
      const url: string = `/SiteAdmin/SavePageVersion`;
      const hasColour = (version.PageBackgroundColour ?? "").trim().length > 0;
      const hasImage = (version.PageBackgroundImage ?? "").trim().length > 0;
      const pageBackgroundType = hasColour && hasImage
        ? "colour-image"
        : hasImage
          ? "image"
          : hasColour
            ? "colour"
            : "none";

      const payload = {
        PageVersionId: version.Id,
        PageId: version.PageId,
        Version: version.Version,
        Status: version.Status,
        CreatedBy: version.CreatedBy,
        DateCreated: version.DateCreated,
        ModifiedBy: version.ModifiedBy,
        DateModified: version.DateModified,
        ViewLayout: version.ViewLayout,
        Username: this.userId,
        DisplayBreadcrumbs: version.DisplayBreadcrumbs,
        PageStyle: version.PageStyle,
        PageScript: version.PageScript,
        PageClass: version.PageClass,
        PageBackgroundColour: version.PageBackgroundColour,
        PageBackgroundImage: version.PageBackgroundImage,
        PageBackgroundImageStyle: version.PageBackgroundImageStyle,
        PageBackgroundImagePosition: version.PageBackgroundImagePosition,
        PageBackgroundImageAttachment: version.PageBackgroundImageAttachment,
        PageBackgroundImageRepeat: version.PageBackgroundImageRepeat,
        PageBackgroundOverlayColour: version.PageBackgroundOverlayColour,
        PageBackgroundOverlayOpacity: version.PageBackgroundOverlayOpacity,
        PageBackgroundType: pageBackgroundType,
      };
      const res = await this._ajaxService.Post<IApiResponse>(url, payload, true);

      if (res?.Success) {
        actionEmitter.emit(EmitEvent.Result, true, this.getMessage(res, "Version details saved"));
        return res.ReturnData ?? version;
      }

      this.EmitDataError(res, "Failed to save version details");
    }

    return null;
  }

  public publishVersion = async (versionid: number): Promise<IVersion | null> => {
    if (this.siteKey) {
      const url = `/SiteAdmin/PublishVersion/${this.siteKey}/${versionid}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.StatusCode === 200 && res.ReturnData) {
        window.dispatchEvent(new Event("cms-publication-changed"));
        return res.ReturnData;
      } else {
        this.EmitDataError(res, "Failed to publish version");
      }
    }
    return null;
  }

  public unPublishVersion = async (versionid: number): Promise<IVersion | null> => {
    if (this.siteKey) {
      const url = `/SiteAdmin/UnPublishVersion/${this.siteKey}/${versionid}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.StatusCode === 200 && res.ReturnData) {
        window.dispatchEvent(new Event("cms-publication-changed"));
        return res.ReturnData;
      } else {
        this.EmitDataError(res, "Failed to unpublish version");
      }
    }
    return null;
  }

  public deleteVersion = async (versionid: number): Promise<IActionResult> => {
    const result: IActionResult = {
      Success: false,
      Message: "Failed to remove the version content"
    };

    if (this.siteKey) {
      const url = `/SiteAdmin/DeleteVersion/${this.siteKey}/${versionid}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.Success) {
        result.Success = true;
        result.Message = this.getMessage(res, "Content version removed");
      } else {
        result.Message = this.getMessage(res, result.Message);
      }

    }
    return result;
  }

  public cloneVersionSamePage= async (versionid: number) : Promise<IVersion | null> => {
    if (this.siteKey) {
      const url: string = `/SiteAdmin/CloneVersionSamePage`;
      const model = {
        VersionId: versionid,
        SiteId: this.siteKey
      };

      const res = await this._ajaxService.Post<IApiResponse>(url, model, true);

      if (res?.StatusCode === 200 && res.ReturnData) {
        return res.ReturnData;
      } else {
        this.EmitDataError(res, "Failed to create a new version");
      }
    }
    return null;
  }

  public cloneVersionNewPage = async (versionid: number, page: AddUpdatePageCommand) : Promise<IActionResult> => {
    const result: IActionResult = {
      Success: false,
      Message: "Failed to create a new page with cloned content",
    };

    if (this.siteKey && page) {
      const url: string = "/SiteAdmin/CloneVersionNewPage";
      const model: ICloneNewPage = {
        VersionId: versionid,
        Alias: page.Alias,
        FooterId: page.FooterId ?? null,
        HeaderId: page.HeaderId ?? null,
        IsNewsPage: page.IsNewsPage,
        Language: page.Language ?? "en-gb",
        ParentPageId: page.ParentPageId ?? null,
        ParentUrl: page.ParentUrl ?? null,
        SiteId: this.siteKey,
        Slug: page.Slug,
        Url: page.Url,
        Title: page.Title
      };

      const res = await this._ajaxService.Post<IApiResponse>(url, model, true);

      if (res?.StatusCode === 200 && res.ReturnData) {
        result.Success = true;
        result.Message = res.Messages?.[0] ?? "Version content cloned to new page";
        return res.ReturnData;
      } else {
        this.EmitDataError(res, "Failed to create a new version");
      }
    }
    return result;
  }

  // HEADERS
  public getSiteHeaders = async (): Promise<IHeader[]> => {
    let headers = [];

    if (this.siteKey) {
      const url = `/SiteAdmin/GetHeaders/${this.siteKey}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.Success && res?.ReturnData) {
        headers = res.ReturnData;
      }
    }
    return headers;
  }

  // FOOTERS
  public getSiteFooters = async (): Promise<IFooter[]> => {
    let footers = [];

    if (this.siteKey) {
      const url = `/SiteAdmin/GetFooters/${this.siteKey}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.Success && res?.ReturnData) {
        footers = res.ReturnData;
      }
    }
    return footers;
  }

  // LANGUAGES
  public getSiteLanguages = async (): Promise<ILanguage[]> => {
    let langs = [];

    if (this.siteKey) {
      const url = `/SiteAdmin/GetLanguages/${this.siteKey}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);
      if (res?.Success && res?.ReturnData) {
        langs = res.ReturnData;
      }
    }
    return langs;
  }

  // COMPONENTS
  public getComponents = async (): Promise<IComponent[]> => {
    let components: IComponent[] = [];

    if (this.siteKey) {
      const url: string = "/Common/GetComponents";
      const res = await this._ajaxService.Get<IApiResponse>(url, true);
      if (res?.StatusCode) {
        components = res.ReturnData;
      }
    }
    return components;
  }

  // LAYOUT TEMPLATES
  public getLayoutTemplates = async (): Promise<ILayoutTemplate[]> => {
    let templates: ILayoutTemplate[] = [];

    if (this.siteKey) {
      const url: string = `/Common/GetLayoutTemplates`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);
      if (res?.StatusCode) {
        templates = res.ReturnData;
      }
    }
    return templates;
  }


  private getMessage = (response?: IApiResponse | null | undefined, defaultmessage?: string): string => {
    let msg: string = defaultmessage ?? "Action failed, please try again";

    if (response?.Messages?.length) {
      msg = response.Messages[0];
    }

    return msg;
  }
}
